// Mapping between canonical DataTypeEntity and UI model for Data Retention Schedule
import { DataRetentionSchedule } from '@/js/external-js/variableModules/namespaces/DataRetentionSchedule.namespace';

export interface DataRetentionRecordUI {
  dataTypeID: number;
  dataTypeName: string;
  dataTypeJustification: string;
  dataRetentionPeriodMin: number;
  dataRetentionPeriodMax: number;
  dataRetentionPeriodUnit?: string; // 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'years'
  dataTypeModeOfCollection: string;
  isPersonalInformation: boolean;
  suppliers: string[];
  assets: string[];
  status: 'Active' | 'Inactive';
}


// Patch DataTypeEntity to allow unit for type safety
export type DataTypeEntityWithUnit = Omit<DataRetentionSchedule.DataTypeEntity, 'dataRetentionPeriod'> & {
  dataRetentionPeriod?: {
    min?: number;
    max?: number;
    unit?: string;
  };
};

export function toUI(entity: DataTypeEntityWithUnit): DataRetentionRecordUI {
  // Heuristic: prefer largest unit that divides min/max exactly
  function guessUnit(val: number | undefined): string {
    if (!val || val === 0) return 'days';
    if (val % 525600 === 0) return 'years'; // 1 year = 525600 minutes
    if (val % 43800 === 0) return 'months'; // 1 month = 43800 minutes
    if (val % 10080 === 0) return 'weeks'; // 1 week = 10080 minutes
    if (val % 1440 === 0) return 'days'; // 1 day = 1440 minutes
    if (val % 60 === 0) return 'hours'; // 1 hour = 60 minutes
    return 'minutes';
  }

  // Convert days to selected unit
  function convertFromDays(days: number, unit: string): number {
    switch (unit) {
      case 'years': return Math.round(days / 365);
      case 'months': return Math.round(days / 30);
      case 'weeks': return Math.round(days / 7);
      case 'days': return days;
      case 'hours': return days * 24;
      case 'minutes': return days * 24 * 60;
      default: return days;
    }
  }

  const minDays = entity.dataRetentionPeriod?.min ?? 0;
  const maxDays = entity.dataRetentionPeriod?.max ?? 0;
  // Use stored unit if present, otherwise guess for legacy
  const unit = entity.dataRetentionPeriod?.unit || guessUnit(minDays);

  return {
    dataTypeID: entity.dataTypeID,
    dataTypeName: entity.dataTypeName,
    dataTypeJustification: entity.dataTypeJustification,
    dataRetentionPeriodMin: convertFromDays(minDays, unit),
    dataRetentionPeriodMax: convertFromDays(maxDays, unit),
    dataRetentionPeriodUnit: unit,
    dataTypeModeOfCollection: entity.dataTypeModeOfCollection,
    isPersonalInformation: entity.isPersonalInformation,
    suppliers: (entity as any).suppliers ?? [],
    assets: (entity as any).assets ?? [],
    // Use the new status field if available, otherwise default to Active
    // deleted field is now only for soft-delete (hiding from view)
    status: entity.status === 'inactive' ? 'Inactive' : 'Active',
  };
}

export function blankDataRetentionRecordUI(): DataRetentionRecordUI {
  return {
    dataTypeID: Math.floor(Math.random() * 1000000),
    dataTypeName: '',
    dataTypeJustification: '',
    dataRetentionPeriodMin: 1,
    dataRetentionPeriodMax: 1,
    dataRetentionPeriodUnit: 'years',
    dataTypeModeOfCollection: '',
    isPersonalInformation: false,
    suppliers: [],
    assets: [],
    status: 'Active',
  };
}

export function fromUI(ui: DataRetentionRecordUI): DataRetentionSchedule.DataTypeEntity {
  // Convert min/max/unit to days for legacy backend
  function toDays(val: number, unit: string | undefined): number {
    switch (unit) {
      case 'minutes': return val / 60 / 24;
      case 'hours': return val / 24;
      case 'days': return val;
      case 'weeks': return val * 7;
      case 'months': return val * 30;
      case 'years': return val * 365;
      default: return val;
    }
  }
  return {
    dataTypeID: ui.dataTypeID,
    dataTypeName: ui.dataTypeName,
    dataTypeJustification: ui.dataTypeJustification,
    dataRetentionPeriod: {
      min: toDays(ui.dataRetentionPeriodMin, ui.dataRetentionPeriodUnit),
      max: toDays(ui.dataRetentionPeriodMax, ui.dataRetentionPeriodUnit),
      unit: ui.dataRetentionPeriodUnit,
    },
    dataTypeModeOfCollection: ui.dataTypeModeOfCollection,
    isPersonalInformation: ui.isPersonalInformation,
    dataTypeMetadata: {
      addedBy: '',
      dateAdded: new Date(),
      dateLastUpdated: new Date(),
    },
    deleted: false, // deleted is now only set by the delete action, not by status
    status: ui.status === 'Inactive' ? 'inactive' : 'active',
    ...(ui.assets ? { assets: ui.assets } : {}),
    ...(ui.suppliers ? { suppliers: ui.suppliers } : {}),
  } as DataRetentionSchedule.DataTypeEntity;
}
